<!--
================================================
Affiche le graphique de la report View (basé sur chart.js)
  * ⚠️ Attention à la  version de la doc de chart.js (prendre la version 2 https://www.chartjs.org/docs/2.9.4/axes/labelling.html)

Url d'accès : http://localhost:8080/fr/cronocloud-inc/reports/summary/startDate=/endDate=/

LIMITATIONS :
  * ⚠️ Ce composant ce comporte étrangement : lors d'un chargement initial de la page reports le chart rest vide (cela fonctionne si on change de période par la suite). Pour palier ce problème la solution à été d'afficher le tableau caché #hiddenChartData dans ReportsView affichant le contenu de this.timerecordsOfPeriod.

  * Le tableau caché #hiddenChartData sert aussi au test cypress (ne pouvant pas tester les valeurs directement dans le chart)


================================================
-->


<!------------------------------------------------
+++++++++++++++++++++ HTML +++++++++++++++++++++++
-------------------------------------------------->

<template>
    <div id="BarChartComp" v-bind:class="{'darkMode':themeToDisplay=='dark'}">
        <h3>{{$t('activitéDuXauX',{'dateDébut':formatDateWithDayName(periodStartDate,locale),'dateFin':formatDateWithDayName(periodEndDate, locale)})}}</h3>

        <div class="loaderContainer"
             v-show="loadingData">
            <spinner-loader-comp v-bind:loading-txt="$t('récupérationDesDonnées')+'...'"></spinner-loader-comp>
        </div>
        <div id="barChartContainer"  class="chartContainer" v-show="!loadingData">
            <canvas  id="chart"></canvas>
        </div>
  </div>
</template>

<!------------------------------------------------
+++++++++++++++++++ JAVASCRIPT++++++++++++++++++++
-------------------------------------------------->


<script>
    import {mapState} from 'vuex'
    import SentryMixin from "../../mixins/SentryMixin.js";
    import TimeMixin from "../../mixins/TimeMixin";
    import SpinnerLoaderComp from "../SpinnerLoaderComp";
    import GlobalErrorNotification from "../../mixins/GlobalErrorNotificationMixin.js";
    import InterceptorMixin from "../../mixins/InterceptorMixin.js";
    import Chart from "chart.js";
    import _ from "lodash";

    export default {
        name: 'BarChartComp', //utile dans vue dev tool
        mixins: [GlobalErrorNotification, SentryMixin, InterceptorMixin,TimeMixin],
        components: {SpinnerLoaderComp},

        props: {
            timerecordsOfPeriod: Object,
            periodStartDate : String,
            periodEndDate : String,
        },
        data: function () {
            return {
                locale : this.$route.params.locale
                ,componentIsReady : false, // indique quand le composant est prêt à être affiché

                chartData: { // données du chart
                    values: null,  // values pour afficher dans le chart
                    labels: null, // labels pour afficher dans le chart
                },
                currentChart: null, // object chartJS du graphique actuellement affiché
                loadingData: false// bool indique si une requête de récupération de données est en cours
                // =================================================================
            }
        },

        computed: {

          // computed props for store :
          ...mapState({
            // user: state => state.user,
            themeToDisplay: state => state.app.themeToDisplay
          }),

          // pour gérer les couleurs selon le thème
          chartColors: {
            get: function () {
              let colors = {
                light: { bg: 'rgba(0,114,187,0.5)', border: '#0072bb', txt:'#333'},
                dark: {bg:'rgba(0,114,187,0.5)', border: '#79a0c3', txt:'#ccc'}
              }
              if (this.themeToDisplay == 'dark') { return colors.dark } else {return colors.light}
            }
          }
        },
        watch: {
            timerecordsOfPeriod: function () {
                if(this.timerecordsOfPeriod != null){
                // if(!_.isEmpty(this.timerecordsOfPeriod)){
                    this.durationsByPeriodUnit()
                }
            }
        },


        methods: {
            /**
             * Calcule les durées par unité de période.
             *
             * Transforme un objet contenant tous les tr de la période (groupés dans une clé portant la date du jour) en un tableau contenant la durée pour le jour (unité).
             */
            durationsByPeriodUnit(){
                let dayDurations = {};

                // itération sur les tr
                for (let [key,value] of Object.entries(this.timerecordsOfPeriod)) {

                    // pour chaque clé on va calculer le total de tous les tr
                    let self = this

                    // on boucle sur tous les tr du jour...
                    let durationOfDay = null; // durée totale du jour en boucle
                    let i = 1;

                    // Si la clé du jour ne boucle ne contient aucun tr on définit une durée null
                    if(_.isEmpty(value,true)){
                        dayDurations[key] = {duration4Human: null,duration: null}
                    }
                    else{ // sinon calcule de la durée
                        value.forEach(function (tr) {
                            let duration = self.calculateDuration(self.$moment,tr.timeIn,tr.timeOut);

                            // au premier tour on ajoute l'objet moment duration au total
                            if(i === 1){
                                durationOfDay = self.$moment.duration(duration);
                            }
                            else { // aux autres tours on additionne
                                durationOfDay = durationOfDay.add(duration);
                            }
                            i++;

                            dayDurations[key] = {
                                duration4Human: self.formatDuration(durationOfDay,self.$moment,self.locale),
                                duration: durationOfDay._milliseconds // ici récupère les millisecondes dans l'objet duration
                            };

                        });
                    }

                }

                this.formatData(dayDurations)
            },

            /**
             * Formate datas pour afficher les labels et les valeurs dans le chart
             * Splite l'objet dayDurations en 2 avec d'un côté les clés et de l'autre les valeurs
             * @param datas
             */
            formatData(datas){
                let labels = []; let values = [];
                let self = this
                _.forEach(datas, function(value, key) {
                    labels.push(
                        [
                        self.formatDateWithDayName(key,self.locale,false),
                        value.duration4Human !=null ? value.duration4Human : self.$t('0h') // si durée vide
                    ])

                    values.push(value.duration / 3600000) // = milliseconde / on divise par 10 000 pour rendre plus lisible pour un humain
                });
                this.chartData.labels = labels
                this.chartData.values = values
                this.displayChart()
            },



            /**
             * Affiche le graphique chart js
             * Inspiré de https://alligator.io/vuejs/vue-chart-js/
             *
             * @param chartId {string} Identifiant du chart
             */
              displayChart() {
                // si un objet currentChart existe déjà on le supprime (évite bug multi charts)
                this.currentChart != null ? this.currentChart.destroy() : '';

                const ctx = document.getElementById('chart');
                ctx.height = 100; // hauteur du chart

                let self = this
                const myChart = new Chart(ctx, {
                    type: 'bar',
                    data: {
                        labels:self.chartData.labels,
                        datasets: [{
                            data: self.chartData.values,
                            backgroundColor: self.chartColors.bg,
                            borderColor: self.chartColors,
                            borderWidth: 1
                        }]
                    },
                    options: {
                        legend: //https://www.chartjs.org/docs/latest/configuration/legend.html
                            {display: false,},
                        responsive: true,
                        maintainAspectRatio: true,
                        scales: {

                            yAxes: [{
                                ticks: {
                                  fontColor:self.chartColors.txt, // Pour styliser les lablels ⚠️ attention à bien prendre la doc de la version 2️ https://www.chartjs.org/docs/2.9.4/axes/labelling.html

                                  beginAtZero: true
                                    ,callback: function(value) {
                                        return self.$t('xh',{'nbHeure':value})
                                    },
                                },
                            }],
                            xAxes: [{
                                gridLines:{display: false},
                                ticks: {
                                  fontColor:self.chartColors.txt, // Pour styliser les lablels ⚠️ attention à bien prendre la doc de la version 2️ https://www.chartjs.org/docs/2.9.4/axes/labelling.html
                                  autoSkip: true,
                                    maxTicksLimit: 12 // on limite le nombre de labels affiché sur l'axe x / @todo à dynamiser selon periode
                                }
                            }]
                        }

                        ,tooltips: {
                            enabled:true,
                            callbacks: {  // pour customiser la tooltip vu sur https://stackoverflow.com/questions/43604597/how-to-customize-the-tooltip-of-a-chart-js-2-0-doughnut-chart
                                title: function(tooltipItem, data) {
                                    return data['labels'][tooltipItem[0]['index']];
                                },
                                label: function(){return null},
                            },
                        },
                    }
                });

                this.currentChart = myChart
                return myChart
            },


        },

        // LIFECYCLE HOOKS (https://v2.vuejs.org/v2/guide/instance.html#Lifecycle-Diagram)
        mounted: function () {


            /*
            * Event report_view_fetching_data, pour affichage du loader
            */
            this.$root.$on('report_view_fetching_data', (isLoading) => {
                isLoading ? this.loadingData = true : this.loadingData = false;
            })

            /*
            * Event report_period_changed, pour actualisation du chart
            */
            this.$root.$on('report_period_changed', () => {
               this.displayChart()
            })


        }
    }
</script>

<!------------------------------------------------
++++++++++++++++++++++ CSS +++++++++++++++++++++++
-------------------------------------------------->

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
    @import "../../assets/css/globalVar";
    /*@import "../assets/css/bulmaResponsiveDep.scss";*/
    #BarChartComp{

      //////////////////////////////////////////
      // LIGHT THEME ///////////////////////////
      h3{
        margin-bottom: 1rem;
      }

      /////////////////////////////////////////
      // DARK THEME ///////////////////////////
      &.darkMode {

      }

    }

</style>
